import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import * as monitoring from '../monitoring/monitoringApi'
import type { Client, Company } from '../monitoring/monitoringApi'

type WorkspaceState = {
  companies: Company[]
  clients: Client[]
  companyId: string
  clientId: string
  company?: Company
  client?: Client
  loading: boolean
  error: string
  selectCompany: (id: string) => void
  selectClient: (id: string) => void
  reload: () => Promise<void>
}

const WorkspaceContext = createContext<WorkspaceState | null>(null)
const companyKey = 'spectra.workspace.company'
const clientKey = 'spectra.workspace.client'

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const [clientRevision, setClientRevision] = useState(0)
  const [companies, setCompanies] = useState<Company[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [companyId, setCompanyId] = useState(() => localStorage.getItem(companyKey) ?? '')
  const [clientId, setClientId] = useState(() => localStorage.getItem(clientKey) ?? '')
  const [loading, setLoading] = useState(true)
  const [companiesLoading, setCompaniesLoading] = useState(true)
  const [error, setError] = useState('')

  async function reload() {
    setCompaniesLoading(true)
    setError('')
    try {
      const nextCompanies = await monitoring.listCompanies()
      setCompanies(nextCompanies)
      setCompanyId((current) => nextCompanies.some((item) => item.id === current) ? current : nextCompanies[0]?.id ?? '')
    } catch {
      setError('As empresas não puderam ser carregadas.')
    } finally {
      setCompaniesLoading(false)
      setClientRevision((current) => current + 1)
    }
  }

  useEffect(() => { void reload() }, [])
  useEffect(() => {
    if (!companyId) { setClients([]); setClientId(''); setLoading(false); return }
    localStorage.setItem(companyKey, companyId)
    setLoading(true)
    setError('')
    let active = true
    monitoring.listClients(companyId).then((items) => {
      if (!active) return
      setClients(items)
      setClientId((current) => items.some((item) => item.id === current) ? current : items.find((item) => !item.disabled)?.id ?? items[0]?.id ?? '')
    }).catch(() => { if (active) setError('Os clientes não puderam ser carregados.') }).finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [companyId, clientRevision, pathname])
  useEffect(() => { if (clientId) localStorage.setItem(clientKey, clientId) }, [clientId])

  const value = useMemo<WorkspaceState>(() => ({
    companies, clients, companyId, clientId,
    company: companies.find((item) => item.id === companyId),
    client: clients.find((item) => item.id === clientId),
    loading: loading || companiesLoading, error, selectCompany: setCompanyId, selectClient: setClientId, reload,
  }), [companies, clients, companyId, clientId, loading, companiesLoading, error])
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
}

export function useWorkspace() {
  const value = useContext(WorkspaceContext)
  if (!value) throw new Error('WorkspaceProvider ausente')
  return value
}
